import { useEffect, useState } from 'react';

import { cn, getMediaUrl } from '../lib/utils';

/**
 * Component for rendering image messages
 */
type EhImageMessageProps = {
  blur?: boolean;
  pendingMediaPlaceholder?: string;
  charFullName: string;
  mediaPathOrUrl: string;
  description?: string;
  // prompt?: string;
  userToken: string;
};

export default function EhImageMessage({
  blur,
  pendingMediaPlaceholder,
  charFullName = 'Character',
  mediaPathOrUrl,
  description,
  userToken
}: EhImageMessageProps) {
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // For public bucket, we can use getMediaUrl directly
  const isPublic = mediaPathOrUrl.startsWith('publicm/') || /^(https?:\/\/)/.test(mediaPathOrUrl);
  const storageUrl = getMediaUrl(mediaPathOrUrl);

  // For private buckets, fetch the authenticated image
  useEffect(() => {
    let isMounted = true;
    
    if (isPublic || !userToken) return;
    
    async function fetchAuthenticatedImage() {
      try {
        const response = await fetch(storageUrl, {
          headers: {  'Authorization': `Bearer ${userToken}` }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        if (isMounted) {
          // Create object URL from blob
          const objectUrl = URL.createObjectURL(blob);
          setImageObjectUrl(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error('[DEV] Error loading authenticated image:', err);
        if (isMounted) {
          setError(import.meta.env.DEV && err instanceof Error ? `[DEV] ${err.message}` : 'Failed to load image');
          setLoading(false);
        }
      }
    }
    
    fetchAuthenticatedImage();
    
    // Clean up function
    return () => {
      isMounted = false;
      // Revoke the object URL to free memory when component unmounts
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaPathOrUrl, userToken, isPublic, storageUrl/*, imageObjectUrl*/]);
  
  // Use either direct URL for public images or object URL for private images
  const imageUrl = isPublic ? storageUrl : imageObjectUrl;
  
  return (
    <div className="relative max-w-full rounded-lg overflow-hidden">
      {(!!pendingMediaPlaceholder || loading || error) && (
        <div className="relative z-10 rounded-lg overflow-hidden p-4 aspect-[13/19] w-[50vw] max-w-full">
          {(!!pendingMediaPlaceholder || imageUrl) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-800/80 select-none">
              <div className="w-10 h-10 border-4 border-zinc-700 border-t-pink-500 rounded-full animate-spin"></div>
              {pendingMediaPlaceholder && (
                <div className="text-zinc-300 text-md my-4">{pendingMediaPlaceholder}</div>
              )}
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-800/80 p-4 text-center">
              <div className="text-red-400 text-sm">
                <span>Failed to load image</span>
              </div>
            </div>
          )}
        </div>
      )}
      {imageUrl && (
        <img
          src={imageUrl}
          alt={description ? `${charFullName}: ${description}` : `Generated image for ${charFullName}`}
          className={cn(
            'rounded-lg max-w-full transition-opacity duration-300',
            !loading ? 'opacity-100' : 'opacity-0',
            blur ? 'blur-md' : '',
            pendingMediaPlaceholder ? 'absolute inset-0' : ''
          )}
          style={{ maxHeight: 'calc(70vh)' }}
          onLoad={() => setLoading(false)}
        />
      )}
      {/* {prompt && (
        <div className="mt-2 text-sm text-zinc-300 italic">
          <span>{prompt}</span>
        </div>
      )} */}
    </div>
  );
} 